'use client'

import React from 'react'
import type { Media } from '@/payload-types'
import { CTAButton } from '@/components/ui/cta-button'
import { mapLegacyAppearance, type LinkFieldType } from '@/fields/link'
import { resolveHref } from '@/utilities/resolveHref'
import { useHubSpotChat } from '@/hooks/useHubSpotChat'

type CardStyleTopContentProps = {
  title: string
  backgroundImage?: Media | string
  description?: string
  cta?: LinkFieldType
  isExpanded?: boolean
  onToggle?: () => void
  cardIndex?: number
}

export const CardStyleTopContent: React.FC<CardStyleTopContentProps> = ({
  title,
  backgroundImage: _backgroundImage, // Ignored - Top Content style doesn't use background images
  description,
  cta,
  isExpanded: _isExpanded = false,
  onToggle: _onToggle,
}) => {
  const { openChat } = useHubSpotChat()

  return (
    <div className="w-full lg:flex-1">
      {/* Mobile: Static card matching Figma design */}
      <div className="lg:hidden">
        <div className="box-border content-stretch flex flex-col items-start p-6 relative rounded-[16px] w-full h-[250px] border border-[#d1ccbe] bg-white overflow-hidden isolate">
          <div className="content-stretch flex flex-col gap-[100px] h-full items-start relative z-10 shrink-0 w-full">
            {/* Content area */}
            <div className="content-stretch flex flex-col gap-[9px] items-start relative shrink-0 text-black w-full">
              <div className="subheading-large text-black w-full">
                <p className="leading-7 mb-0">{title}</p>
              </div>
              {description && (
                <div className="body-copy-medium text-black/70 w-full">
                  <p className="leading-[22px] mb-0">{description}</p>
                </div>
              )}
            </div>

            {/* CTA at bottom */}
            {cta?.label && (cta?.url || cta?.reference || cta?.type === 'chat') && (
              <div className="mt-auto">
                <CTAButton
                  variant={mapLegacyAppearance(cta.appearance)}
                  href={cta?.type === 'chat' ? undefined : resolveHref(cta)}
                  newTab={cta.newTab || false}
                  onClick={
                    cta?.type === 'chat'
                      ? async (e) => {
                          e.preventDefault()
                          await openChat()
                        }
                      : undefined
                  }
                >
                  {cta.label}
                </CTAButton>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop: Static card design matching Figma (350px height) */}
      <div className="hidden lg:block">
        <div
          className="box-border content-stretch flex flex-col items-start justify-between p-6 relative rounded-[16px] shrink-0 w-full h-[350px] border border-[#d1ccbe] bg-white hover:bg-brand-light transition-colors duration-300 group overflow-hidden isolate"
        >
          <div className="content-stretch flex flex-col gap-[9px] items-start relative z-10 shrink-0 text-black w-full">
            <div className="subheading-large text-black w-full">
              <p className="leading-7 mb-0">{title}</p>
            </div>
            {description && (
              <div className="body-copy-medium text-black/70 w-full">
                <p className="leading-[22px] mb-0">{description}</p>
              </div>
            )}
          </div>

          {/* CTA at bottom */}
          {cta?.label && (cta?.url || cta?.reference || cta?.type === 'chat') && (
            <div className="mt-auto relative z-10">
              <CTAButton
                variant={mapLegacyAppearance(cta.appearance)}
                href={cta?.type === 'chat' ? undefined : resolveHref(cta)}
                newTab={cta.newTab || false}
                onClick={
                  cta?.type === 'chat'
                    ? async (e) => {
                        e.preventDefault()
                        await openChat()
                      }
                    : undefined
                }
              >
                {cta.label}
              </CTAButton>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}